import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { AuthService } from '../../services/auth.service';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { PrivacyPolicyService } from 'app/services/module-config/privacy-policies/privacy-policie.service';

@Component({
  standalone: false,
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css'],
})
export class LoginComponent implements OnInit, OnDestroy {
  correo: string = '';
  contrasena: string = '';
  errorMsg: string = '';
  recuerdame: boolean = false;
  mostrarPassword = false;
  companies: any[] = [];
  choosingCompany = false;
  companyLoading = false;
  companyError = '';

  private redirectUrl: string | null = null;

  privacyModalOpen = false;
  privacyAccepted = false;
  privacySubmitting = false;
  privacyLoadingPdf = false;

  pendingPrivacyPolicy: any = null;
  privacyPdfSafeUrl: SafeResourceUrl | null = null;
  private privacyPdfObjectUrl: string | null = null;

  constructor(
    private authService: AuthService,
    private privacyPolicyService: PrivacyPolicyService,
    private router: Router,
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private http: HttpClient,
  ) {
    this.route.queryParamMap.subscribe((params) => {
      this.redirectUrl = this.safeRedirectUrl(params.get('redirect'));
    });
  }
  ngOnInit(): void {
    this.redirectIfAlreadyLoggedIn();
  }

  /**
   * Evita que un usuario autenticado vuelva a visualizar el login.
   */
  private redirectIfAlreadyLoggedIn(): void {
    if (this.authService.hasValidToken()) {
      void this.router.navigateByUrl(this.redirectUrl || '/home', {
        replaceUrl: true,
      });
      return;
    }

    // Si quedó un token vencido o inválido, permanecer en login y conservar
    // el parámetro redirect para usarlo después de autenticar.
    if (localStorage.getItem('token')) {
      localStorage.removeItem('token');
    }
  }
  ngOnDestroy(): void {
    this.revokePrivacyPdfUrl();
    document.body.classList.remove('login-policy-modal-open');
  }

  iniciarSesion(): void {
    if (this.companyLoading || this.choosingCompany) return;
    this.errorMsg = '';

    this.authService.login(this.correo, this.contrasena, this.recuerdame).subscribe({
      next: (res) => {
        localStorage.setItem('token', res.token);

        if (res?.birthday?.is_today) {
          sessionStorage.setItem(
            'showBirthdayBalloons',
            JSON.stringify({ show: true }),
          );
        } else {
          sessionStorage.removeItem('showBirthdayBalloons');
        }

        const privacyPolicyState = res?.privacy_policy;

        if (
          privacyPolicyState?.required &&
          privacyPolicyState?.policy?.external_id
        ) {
          this.openPrivacyPolicyModal(privacyPolicyState.policy);
          return;
        }

        this.continueAfterPrivacyPolicy();
      },
      error: (err) => {
        this.errorMsg = err.error?.error || 'Error al iniciar sesión';
      },
    });
  }

  private openPrivacyPolicyModal(policy: any): void {
    this.pendingPrivacyPolicy = policy;
    this.privacyAccepted = false;
    this.privacyModalOpen = true;
    this.privacyLoadingPdf = true;
    this.privacyPdfSafeUrl = null;

    document.body.classList.add('login-policy-modal-open');

    this.revokePrivacyPdfUrl();

    this.privacyPolicyService.viewEmployeePrivacyPolicy(policy.external_id).subscribe({
      next: (blob) => {
        const pdfBlob = new Blob([blob], { type: 'application/pdf' });

        this.privacyPdfObjectUrl = URL.createObjectURL(pdfBlob);

        const viewerUrl = `${this.privacyPdfObjectUrl}#toolbar=0&navpanes=0&scrollbar=1`;

        this.privacyPdfSafeUrl =
          this.sanitizer.bypassSecurityTrustResourceUrl(viewerUrl);
      },
      error: async (error) => {
        this.errorMsg = await this.getBlobErrorMessage(error);
        this.privacyLoadingPdf = false;
      },
    });
  }

  onPrivacyPdfLoaded(): void {
    this.privacyLoadingPdf = false;
  }

  acceptPrivacyPolicy(): void {
    if (!this.pendingPrivacyPolicy?.external_id) {
      this.errorMsg = 'No se encontró la política de privacidad.';
      return;
    }

    if (!this.privacyAccepted) {
      this.errorMsg = 'Debe aceptar la política de privacidad para continuar.';
      return;
    }

    this.errorMsg = '';
    this.privacySubmitting = true;

    this.privacyPolicyService
      .acceptPolicyFromLogin(this.pendingPrivacyPolicy.external_id)
      .subscribe({
        next: () => {
          this.privacySubmitting = false;
          this.closePrivacyPolicyModal();
          this.continueAfterPrivacyPolicy();
        },
        error: (err) => {
          this.privacySubmitting = false;
          this.errorMsg =
            err.error?.error ||
            err.error?.message ||
            'No se pudo registrar la aceptación de la política.';
        },
      });
  }

  private continueAfterPrivacyPolicy(): void {
    this.companyLoading = true;
    this.companyError = '';
    this.http.get<{data: any[]}>(`${environment.apiUrl}/config/companies/mine`).subscribe({
      next: result => {
        this.companyLoading = false;
        this.companies = result.data.filter(company => company.enabled);
        if (this.companies.length > 1) {
          this.choosingCompany = true;
        } else if (this.companies.length === 1) {
          this.loadPermissionsAndEnter();
        } else {
          this.errorMsg = 'No tienes empresas habilitadas para ingresar.';
        }
      },
      error: () => {
        this.companyLoading = false;
        this.choosingCompany = true;
        this.companyError = 'No se pudieron cargar tus empresas. Reintenta para continuar.';
      },
    });
  }

  retryCompanies(): void { this.continueAfterPrivacyPolicy(); }

  enterCompany(company: any): void {
    if (this.companyLoading || !company.enabled) return;
    this.companyLoading = true;
    this.companyError = '';
    this.http.post<{token: string; permisos: string[]}>(`${environment.apiUrl}/config/companies/switch`, {tenant_id: company.id}).subscribe({
      next: result => {
        this.authService.applyCompanySession(result.token, result.permisos);
        this.companyLoading = false;
        this.choosingCompany = false;
        void this.router.navigateByUrl(this.authService.getUsuarioDesdeToken()?.secondary_company ? '/recursos-humanos/attendance' : (this.redirectUrl || '/home'), {replaceUrl: true});
      },
      error: err => {
        this.companyLoading = false;
        this.companyError = err.error?.error || 'No se pudo ingresar a la empresa. Inténtalo nuevamente.';
      },
    });
  }

  cancelCompanySelection(): void {
    if (this.companyLoading) return;
    this.authService.logout();
    this.choosingCompany = false;
    this.companies = [];
  }

  private loadPermissionsAndEnter(): void {
    this.authService.getPermisosBackend().subscribe({
      next: (permisos) => {
        this.authService.setPermisos(permisos);

        void this.router.navigateByUrl(this.redirectUrl || '/home', {
          replaceUrl: true,
        });
      },
      error: () => {
        this.errorMsg = 'Error cargando permisos';
      },
    });
  }

  private safeRedirectUrl(value: string | null): string | null {
    const target = String(value || '').trim();

    // Solo admitimos rutas internas de ZIVO. Evita redirects externos o un
    // bucle de vuelta al propio login.
    if (!target.startsWith('/') || target.startsWith('//')) return null;
    if (target === '/login' || target.startsWith('/login?')) return null;

    return target;
  }

  logoutFromPolicyModal(): void {
    localStorage.removeItem('token');
    sessionStorage.removeItem('showBirthdayBalloons');
    this.closePrivacyPolicyModal();
    this.pendingPrivacyPolicy = null;
    this.errorMsg = 'Debe aceptar la política de privacidad para ingresar.';
  }

  private closePrivacyPolicyModal(): void {
    this.privacyModalOpen = false;
    this.privacyAccepted = false;
    this.privacySubmitting = false;
    this.privacyLoadingPdf = false;
    this.privacyPdfSafeUrl = null;

    this.revokePrivacyPdfUrl();

    document.body.classList.remove('login-policy-modal-open');
  }

  private revokePrivacyPdfUrl(): void {
    if (this.privacyPdfObjectUrl) {
      URL.revokeObjectURL(this.privacyPdfObjectUrl);
      this.privacyPdfObjectUrl = null;
    }
  }

  private async getBlobErrorMessage(error: any): Promise<string> {
    if (error?.error instanceof Blob) {
      const text = await error.error.text();

      try {
        const parsed = JSON.parse(text);
        return parsed?.message || parsed?.error || text;
      } catch (_) {
        return text || 'No se pudo cargar la política de privacidad.';
      }
    }

    return (
      error?.error?.message ||
      error?.error?.error ||
      'No se pudo cargar la política de privacidad.'
    );
  }
}
